import React, { useEffect, useMemo, useRef } from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { useQuery } from 'convex/react'

import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'

interface Props {
  tournamentId: Id<'tournaments'>
  tournamentState?: string
  isEliminated?: boolean
}

/**
 * Inline per-user results content for the lobby tabs. Renders the user's
 * final placement and per-tournament stats. Previously a standalone screen.
 *
 * No "Return to Lobby" CTA — the user is already in the lobby, just on a
 * different tab.
 */
export function TournamentSummaryView({ tournamentId, tournamentState, isEliminated }: Props) {
  const { setEvent } = useStore().analytics

  const stats = useQuery(api.tournaments.queries.getMyTournamentStats, { tournamentId })

  const summaryViewedRef = useRef(false)
  useEffect(() => {
    if (summaryViewedRef.current) return
    if (!stats) return
    summaryViewedRef.current = true
    setEvent(events.mtt.summary_screen_viewed, {
      tournamentId,
      finalRank: stats.finalRank ?? null,
      totalPopulation: stats.totalPopulation,
      handsPlayed: stats.handsPlayed,
      handsWon: stats.handsWon,
      tableMovesCount: stats.tableMovesCount
    })
  }, [stats, tournamentId, setEvent])

  const elapsedLabel = useMemo(() => {
    if (!stats?.elapsedMs || stats.elapsedMs <= 0) return '—'
    const totalSeconds = Math.floor(stats.elapsedMs / 1000)
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }, [stats?.elapsedMs])

  if (stats === undefined) {
    return (
      <View style={styles.centered}>
        <DS.Text variant="Body Large/Regular" color="Brand/Dark Grey">Loading results…</DS.Text>
      </View>
    )
  }

  if (stats === null) {
    const isTournamentFinished = tournamentState === 'finished'
    const emptyLabel = isTournamentFinished
      ? 'No personal results are available for this tournament.'
      : isEliminated
        ? 'Your tournament results are not ready yet.'
        : 'Results appear once you are eliminated or the tournament finishes.'
    return (
      <View style={styles.centered}>
        <DS.Text variant="Body Large/Regular" color="Brand/Dark Grey" style={{ textAlign: 'center' }}>
          {emptyLabel}
        </DS.Text>
      </View>
    )
  }

  const placeLabel = stats.finalRank
    ? `${ordinal(stats.finalRank)} of ${stats.totalPopulation}`
    : '—'

  return (
    <View style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={styles.content} style={{ flex: 1 }}>
        <DS.Frame gap="20">
          {/*
            Summary box doubles as the screen header. The previous
            "You are out" / "Your Results" heading above this card was
            redundant — the Final Place card already communicates outcome.
          */}
          <View style={styles.placeCard}>
            <DS.Text variant="Mini/Bold" style={styles.eyebrow}>
              Final Place
            </DS.Text>
            <DS.Text variant="Heading 1/Medium">{placeLabel}</DS.Text>
          </View>

          <DS.Frame gap="12">
            <StatRow label="Hands Played" value={String(stats.handsPlayed)} />
            <StatRow label="Hands Won" value={String(stats.handsWon)} />
            <StatRow label="Peak Chip Stack" value={stats.peakChips.toLocaleString()} />
            <StatRow label="Time at Tables" value={elapsedLabel} />
            <StatRow
              label="Tables Played"
              value={String(stats.tableMovesCount + 1)}
              hint={stats.tableMovesCount > 0 ? `${stats.tableMovesCount} move${stats.tableMovesCount === 1 ? '' : 's'}` : undefined}
            />
            <StatRow
              label="Made it to Blind Level"
              value={
                stats.finalBlindLevelIndex !== null
                  ? String(stats.finalBlindLevelIndex + 1)
                  : '—'
              }
            />
          </DS.Frame>
        </DS.Frame>
      </ScrollView>
    </View>
  )
}

function StatRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <DS.Frame row style={styles.statRow}>
      <DS.Frame gap="4" style={{ flex: 1 }}>
        <DS.Text variant="Body Large/Regular">{label}</DS.Text>
        {hint && (
          <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
            {hint}
          </DS.Text>
        )}
      </DS.Frame>
      <DS.Text variant="Body Large/Medium">{value}</DS.Text>
    </DS.Frame>
  )
}

function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0]}`
}

const styles = StyleSheet.create({
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  // Tighter top padding so the Final Place card sits close under the tab strip.
  content: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 16 },
  eyebrow: {
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: DS.palette['Brand/Maroon']
  },
  placeCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 20,
    gap: 4,
    alignItems: 'flex-start'
  },
  statRow: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    alignItems: 'center'
  }
})
